import { Body1, Body2 } from '@cian/ui-kit';
import { clsx } from 'clsx';
import { IconActionFilters16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';

import { MAINPAGE_SEARCH_URL } from 'shared/constants/constants';

import { useScrollCollapse } from './useScrollCollapse';
import { useTypewriterPlaceholder } from './useTypewriterPlaceholder';

import * as styles from './RedesignSearchBox.css';

export interface IRedesignSearchBoxProps {
  filtersUrl: string;
  onSearchClick(event: React.MouseEvent<HTMLAnchorElement>): void;
  onFiltersClick?(event: React.MouseEvent<HTMLAnchorElement>): void;
  placeholders: string[];
  disablePlaceholder: boolean;
  /** Текст поискового запроса. Если задан, показывается вместо плейсхолдера, а перебор не идёт. */
  searchQuery?: string;
  /** Вызывается, когда строка сворачивается по скроллу. */
  onCollapse?(): void;
  /**
   * Включает анимацию, даже если в системе выставлено «уменьшить движение».
   * Приезжает из настройки BX.SearchStringAnimationForced.Enabled.
   */
  isAnimationForced?: boolean;
}

const PLACEHOLDER = 'Что ищете?';

export const RedesignSearchBox: React.FC<IRedesignSearchBoxProps> = ({
  filtersUrl,
  onSearchClick,
  onFiltersClick,
  placeholders,
  disablePlaceholder = false,
  searchQuery,
  onCollapse,
  isAnimationForced = false,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);

  useScrollCollapse(rootRef, { scrolledClassName: styles['is-scrolled'], onCollapse });

  const typedPlaceholder = useTypewriterPlaceholder(
    placeholders,
    disablePlaceholder || Boolean(searchQuery),
    isAnimationForced,
  );

  const typedText = searchQuery ?? typedPlaceholder ?? PLACEHOLDER;
  const staticText = searchQuery ?? PLACEHOLDER;
  // Запрос пользователя — это контент, а не подсказка, поэтому он в основном цвете текста.
  // Набранный печатной машинкой текст и дефолтный плейсхолдер остаются вторичными.
  const textColor = searchQuery ? 'text-primary-default' : 'text-secondary-default';

  return (
    <div
      ref={rootRef}
      className={clsx(styles['search-box'], isAnimationForced && styles['animation-forced'])}
      data-testid="RedesignSearchBox"
    >
      <a href={MAINPAGE_SEARCH_URL} className={styles['search-field']} onClick={onSearchClick} aria-label={staticText}>
        {/*
          Обе версии плейсхолдера скрыты от скринридера: набирающийся текст менял бы
          доступное имя ссылки на каждый символ, а свёрнутая версия дублировала бы его.
          Вместо них имя задано статичным aria-label выше.
        */}
        <span className={styles['placeholder-expanded']} aria-hidden="true">
          <Body2 color={textColor}>{typedText}</Body2>
        </span>
        <span className={styles['placeholder-collapsed']} aria-hidden="true">
          <Body1 color={textColor}>{staticText}</Body1>
        </span>
      </a>
      <a
        href={filtersUrl}
        className={styles['filters-button']}
        onClick={onFiltersClick}
        data-testid="RedesignSearchBoxFiltersButton"
        aria-label="Фильтры"
      >
        <IconActionFilters16 />
      </a>
    </div>
  );
};

RedesignSearchBox.displayName = 'RedesignSearchBox';
